import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {db} from '../config/firebase';
import {doc, getDoc, collection, query, where,
  getDocs, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import CommentsSection from './CommentsSection';
import AdSlot from '../Monetization/AdSlot';
import {useAuth} from '../Auth/AuthContext';
import {usePlan} from '../context/PlanContext';
import ReactMarkdown from 'react-markdown';
import './RecipeDetailPage.css';
import { formatIngredient } from '../utils/formatIngredient';
import { useFavoriteRecipes } from '../hooks/useFavoriteRecipes';
import { useToast } from '../context/ToastContext';
import saveIcon from '../Icons/save.png';
import savedIcon from '../Icons/saved1.png';
import getRecipeId from '../utils/getRecipeId';
import ImageSwiper from './ImageSwiper';

// Contador de visitas en sesión para la vignette (1 de cada 4)
let _detailVisitCount = 0;

const RecipeDetailPage = () => {
    const { recipeId } = useParams();
    const { userId } = useAuth();
    const { isPremium } = usePlan();
    const { showToast } = useToast();
    const { favoriteRecipesIds, addToFavorites, removeFromFavorites } =
        useFavoriteRecipes(userId);
    const [recipe, setRecipe] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [userNames, setUserNames] = useState({});
    const [showAllComments, setShowAllComments] = useState(false);
    const [showVignette, setShowVignette] = useState(false);

    const fetchComments = async (recipeId) => {
        const commentsQuery = query(collection(db, 'comments'), where('recipeId', '==', recipeId));
        const commentsSnapshot = await getDocs(commentsQuery);
        const commentsList = commentsSnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
        setComments(commentsList);

        // Fetch nombres de los autores de los comentarios
        const uniqueIds = [...new Set(commentsList.map(c => c.userId).filter(Boolean))];
        const nameEntries = await Promise.all(
            uniqueIds.map(async (uid) => {
                const snap = await getDoc(doc(db, 'users', uid));
                return [uid, snap.exists()
                    ? (snap.data().userName || snap.data().name || uid)
                    : uid
                ];
            })
        );
        setUserNames(prev => ({ ...prev, ...Object.fromEntries(nameEntries) }));
   };

    // Vignette: mostrar anuncio pantalla completa 1 de cada 4 visitas (solo básicos)
    useEffect(() => {
        if (isPremium) return;
        _detailVisitCount += 1;
        if (_detailVisitCount % 4 === 0) {
            setShowVignette(true);
        }
    }, [recipeId, isPremium]);

    useEffect(() => {
        const fetchRecipe = async () => {
            // 1. Buscar en la colección pública de recetas
            const recipeDocRef = doc(db, 'recetas', recipeId);
            const recipeDoc = await getDoc(recipeDocRef);
            if (recipeDoc.exists()) {
                setRecipe(recipeDoc.data());
                return;
            }
            // 2. Fallback: buscar en recetasFavoritas del usuario (recetas guardadas desde el chat)
            if (userId) {
                const userSnap = await getDoc(doc(db, 'users', userId));
                if (userSnap.exists()) {
                    const saved = userSnap.data().recetasFavoritas || [];
                    const found = saved.find(r => r.id === recipeId);
                    if (found) { setRecipe(found); return; }
                }
            }
            console.error('Receta no encontrada');
        };

        fetchRecipe();
        fetchComments(recipeId);
    }, [recipeId, userId]);

    // getDoc SOLO del autor — se ejecuta cuando recipe carga y tiene userId
    useEffect(() => {
        const fetchAuthorName = async () => {
            if (!recipe?.userId) return;
            const userSnap = await getDoc(doc(db, 'users', recipe.userId));
            if (userSnap.exists()) {
                const name = userSnap.data().userName || userSnap.data().name || recipe.userId;
                setUserNames(prev => ({...prev, [recipe.userId]: name}));
            }
        };
        fetchAuthorName();
    }, [recipe?.userId]);

    const handleAddComment = async (recipeId, userId, newComment, setNewComment, fetchComments) => {
        if (newComment.trim() === '') return;

        const commentData = {
            recipeId,
            userId,
            text: newComment,
            timestamp: serverTimestamp(),
       };

        await addDoc(collection(db, 'comments'), commentData);
        setNewComment('');
        fetchComments(recipeId);
   };

    const handleEditComment = async (recipeId, commentId, newText) => {
        await updateDoc(doc(db, 'comments', commentId), { text: newText });
        fetchComments(recipeId);
    };

    const handleDeleteComment = async (recipeId, commentId) => {
        await deleteDoc(doc(db, 'comments', commentId));
        fetchComments(recipeId);
        showToast('Comentario eliminado', 'info');
    };

    const toggleComments = () => {
        setShowAllComments(!showAllComments);
   };

    const isFavorite = favoriteRecipesIds.includes(getRecipeId(recipe));      // <- AÑADIR

    const handleToggleFavorite = async () => {                                // <- AÑADIR
        if (!recipe) return;
        if (isFavorite) {
            await removeFromFavorites(recipe);
            showToast('Receta eliminada de favoritos', 'info');
        } else {
            await addToFavorites(recipe);
            showToast('Receta guardada en favoritos ✓', 'success');
        }
    };

    if (!recipe) return <p>Cargando...</p>;

    return (
        <div className="recipe-detail-page">
            {/* Vignette: anuncio pantalla completa 1 de cada 4 visitas */}
            {showVignette && (
                <div className="ad-vignette-overlay">
                    <AdSlot slotId="vignette" format="auto" />
                    <button className="ad-vignette-close" onClick={() => setShowVignette(false)}>
                        Continuar →
                    </button>
                </div>
            )}
            <h2>{recipe.nombre}</h2>

            {/* BOTÓN GUARDAR */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <img
                    src={isFavorite ? savedIcon : saveIcon}
                    alt={isFavorite ? 'Guardada' : 'Guardar receta'}
                    onClick={handleToggleFavorite}
                    style={{ width: 28, height: 28, cursor: 'pointer' }}
                />
            </div>

            {/* Vídeo de la receta (si existe) */}
            {recipe.videoUrl && (
                <video
                    src={recipe.videoUrl}
                    controls
                    playsInline
                    style={{ width: '100%', borderRadius: 10, marginBottom: 12, maxHeight: 320, background: '#000' }}
                />
            )}

            {/* REEMPLAZAR img única por ImageSwiper */}
            <ImageSwiper
                imagenes={recipe.imagenes}
                imagen={recipe.imagen}
                nombre={recipe.nombre}
            />

            <ReactMarkdown>
                {`
**Ingredientes:**
${recipe.ingredientes.map(ing => `- ${formatIngredient(ing)}`).join('\n')}

**Instrucciones:**
${Array.isArray(recipe.instrucciones) ? recipe.instrucciones.join('\n') : recipe.instrucciones}

**Dificultad:** ${recipe.dificultad || 'Media'}

**Tiempo total estimado:** ${recipe.tiempoEstimado ?? recipe.tiempoPreparacion ?? 0} minutos
                `}
            </ReactMarkdown>
            <div className="comments-section-container">
                <CommentsSection
                    recipeId={recipeId}
                    comments={{[recipeId]: comments}}
                    newComment={newComment}
                    setNewComment={setNewComment}
                    handleAddComment={(recipeId) => handleAddComment(recipeId, userId, newComment, setNewComment, fetchComments)}
                    handleEditComment={handleEditComment}
                    handleDeleteComment={handleDeleteComment}
                    userNames={userNames}
                    currentUserId={userId}
                    showAllComments={showAllComments}
                    toggleComments={toggleComments}
                />
            </div>
            {/* Banner inferior de anuncio (solo usuarios básicos) */}
            <AdSlot slotId="detailBanner" format="horizontal" style={{ margin: '16px 0' }} />
        </div>
    );
};

export default RecipeDetailPage;